import { Button } from '@carbon/react';
import { Renew } from '@carbon/icons-react';
import { asProblem, FormModal, hasPermission, problemMessage, useAdmin, type RecordValues } from '@pos/admin-ui';
import { useTranslation } from '@pos/i18n';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { hoApi } from '../api';
import './posButtons.scss';

/** The Buttons tab's own toolbar (FR-CFG-05): *Reset to default* puts back every button the POS draws. */
export function PosButtonsToolbar({ profile, refresh }: { profile: RecordValues; refresh(): void }) {
  const { t } = useTranslation();
  const { session } = useAdmin();
  const [confirming, setConfirming] = useState(false);
  const canEdit = hasPermission(session, 'ho.config.pos_profiles.manage');

  const reset = useMutation({
    mutationFn: async () => {
      const { data, error } = await hoApi.POST('/api/v1/menu/config/pos-profiles/{id}/buttons/reset', {
        params: { path: { id: String(profile.id) } },
      });
      if (!data) throw error;
      return data;
    },
    onSuccess: () => {
      setConfirming(false);
      refresh();
    },
  });

  return (
    <>
      {canEdit && (
        <Button kind="ghost" renderIcon={Renew} onClick={() => setConfirming(true)}>
          {t('posButtons.reset.button')}
        </Button>
      )}
      {reset.isSuccess && (
        <span className="pos-buttons__done" role="status">
          {t('posButtons.reset.done', { count: Number(reset.data.added) })}
        </span>
      )}
      {confirming && (
        <FormModal
          danger
          size="sm"
          title={t('posButtons.reset.title')}
          primaryText={t('posButtons.reset.confirm')}
          pending={reset.isPending}
          error={reset.error ? problemMessage(t, asProblem(reset.error)) : null}
          onSubmit={() => reset.mutate()}
          onClose={() => {
            reset.reset();
            setConfirming(false);
          }}
        >
          <p>{t('posButtons.reset.body', { name: String(profile.name ?? '') })}</p>
        </FormModal>
      )}
    </>
  );
}
